import { useMemo } from 'react';
import { outreachEndpoints } from '../../../store/rtk-query/apis/outreach/outreach.toolkit-api';
import { ConversationStatusEnum } from '../../../store/rtk-query/apis/outreach/enums/conversation-status.enum';
import { ConversationBaseResponseDto } from '../../../store/rtk-query/apis/outreach/dto/response/conversation-base.response';
import {
  ConversationMinimizedResponseDto,
  ConversationsMinimizedResponseDto,
} from '../../../store/rtk-query/apis/outreach/dto/response/conversation-minimized.response';

interface OutreachConversationsHook {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
  conversations: ConversationMinimizedResponseDto[];
  toOutreachConversations: ConversationBaseResponseDto[];
  openTasksConversations: ConversationBaseResponseDto[];
  openTasksCount: number;
  sequenceLength: number;
  rawData: ConversationsMinimizedResponseDto;
}

export const useOutreachConversations = (positionId: string): OutreachConversationsHook => {
  const { useGetConversationsByPositionIdQuery } = outreachEndpoints;
  const result = useGetConversationsByPositionIdQuery({ positionId }, {
    skip: !positionId,
    refetchOnMountOrArgChange: true,
  });

  const {
    data, isLoading, isFetching, isError, refetch,
  } = result;

  const toOutreachConversations = useMemo(() => {
    if (!data?.conversations) return [];

    return data.conversations.filter((conversation) => {
      return conversation.status === ConversationStatusEnum.New &&
        !conversation.isArchived;
    });
  }, [data]);

  const openTasksConversations = useMemo(() => {
    if (!data?.conversations) return [];

    return data.conversations.filter((conversation) => {
      const isCompleted = conversation.sequenceLength &&
        conversation.latestMessage?.sequenceStep === conversation.sequenceLength;
      if (conversation.isArchived || isCompleted) {
        return false;
      }

      return conversation.status === ConversationStatusEnum.New ||
      conversation.status === ConversationStatusEnum.Replied;
    });
  }, [data]);

  const openTasksCount = useMemo(() => {
    return openTasksConversations.length;
  }, [openTasksConversations]);

  const sequenceLength = data?.sequenceLength || 0;

  return {
    isLoading,
    isFetching,
    isError,
    refetch,

    conversations: data?.conversations || [],
    toOutreachConversations,

    openTasksConversations,
    openTasksCount,

    sequenceLength,

    rawData: data,
  };
};
